<template>
  <div class="dataset-preview-modal" v-if="show">
    <div class="modal-overlay" @click="closeModal">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h5><i class="fas fa-satellite"></i> Create Satellite Datasets</h5>
          <button type="button" class="close" @click="closeModal">
            <span>&times;</span>
          </button>
        </div>
        
        <div class="modal-body">
          <!-- Scene Information -->
          <div class="scene-info mb-4">
            <h6><i class="fas fa-info-circle"></i> Scene Details</h6>
            <div class="row">
              <div class="col-md-6">
                <p><strong>Scene ID:</strong> <span class="text-muted small">{{ scene.id }}</span></p>
                <p><strong>{{ sceneDateLabel }}:</strong> {{ sceneDateDisplay }}</p>
                <p v-if="hasCloudCover">
                  <strong>Cloud Cover:</strong> {{ scene.properties['eo:cloud_cover'] }}%
                </p>
                <p v-else-if="isSar">
                  <strong>Polarizations:</strong>
                  {{ (scene.properties['sar:polarizations'] || []).join(' / ') || '—' }}
                </p>
              </div>
              <div class="col-md-6">
                <p><strong>Block:</strong> {{ block.name }}</p>
                <p><strong>Resolution:</strong> {{ sceneResolution }}</p>
                <p><strong>Source:</strong> {{ sceneSourceLabel }}</p>
              </div>
            </div>
          </div>

          <!-- Dataset Configuration -->
          <div class="dataset-config mb-4">
            <h6><i class="fas fa-cog"></i> Dataset Configuration</h6>
            
            <!-- Base Dataset Name -->
            <div class="form-group">
              <label for="baseDatasetName">Base Dataset Name</label>
              <input
                v-model="baseDatasetName"
                id="baseDatasetName"
                class="form-control"
                placeholder="Enter base dataset name..."
              />
              <small class="form-text text-muted">
                Individual datasets will be named: "Base Name - Index Type"
              </small>
            </div>

            <!-- Dataset Description -->
            <div class="form-group">
              <label for="datasetDescription">Description</label>
              <textarea
                v-model="datasetDescription"
                id="datasetDescription"
                class="form-control"
                rows="2"
                placeholder="Enter dataset description..."
              />
            </div>

            <!-- Destination Folder — breadcrumb + nested browser -->
            <div class="form-group">
              <label>Save to Folder</label>
              <div class="folder-picker">
                <!-- Breadcrumb trail -->
                <div class="folder-breadcrumb">
                  <button
                    type="button"
                    class="folder-crumb"
                    :class="{ 'is-current': browseFolderId === null }"
                    @click="goToRootFolder"
                    title="Farm root (click to refresh folder list)"
                  >
                    <i class="fas fa-home"></i>
                  </button>
                  <template v-for="(crumb, i) in folderBreadcrumbs">
                    <span :key="`sep-${i}`" class="folder-crumb-sep">/</span>
                    <button
                      :key="`crumb-${crumb.id}`"
                      type="button"
                      class="folder-crumb"
                      :class="{ 'is-current': browseFolderId === crumb.id }"
                      @click="browseFolderId = crumb.id"
                    >{{ crumb.name }}</button>
                  </template>
                </div>

                <!-- Child folder list -->
                <div class="folder-children">
                  <div
                    v-if="childFolders.length === 0"
                    class="folder-empty"
                  >
                    <small><em>No subfolders here.</em></small>
                  </div>
                  <a
                    v-for="folder in childFolders"
                    :key="folder.id"
                    class="folder-child clickable"
                    @click="browseFolderId = folder.id"
                  >
                    <i class="far fa-folder"></i>
                    <span class="folder-child-name">{{ folder.name }}</span>
                    <i class="fas fa-chevron-right folder-child-chevron"></i>
                  </a>
                </div>

                <!-- Save-here confirmation row -->
                <div class="folder-save-here">
                  <i class="fas fa-arrow-down-to-bracket"></i>
                  <span>Save here:</span>
                  <strong>{{ currentFolderLabel }}</strong>
                </div>
              </div>
            </div>

            <!-- Output Selection -->
            <div class="output-selection mb-4">
              <h6>
                <i class="fas fa-layer-group"></i> Select Outputs
                <span v-if="isSar" class="badge badge-warning ml-2" style="background:#FC3D21;color:#fff;">SAR scene</span>
                <span v-else class="badge badge-info ml-2" style="background:#0B3D91;">Optical scene</span>
              </h6>

              <!-- SAR outputs (Sentinel-1) -->
              <div v-if="isSar" class="output-group mb-3">
                <h6 class="text-danger">📡 SAR Products</h6>
                <p class="small text-muted mb-2">
                  Sentinel-1 C-band Synthetic Aperture Radar. Backscatter is
                  cloud-independent and useful as a soil-moisture / canopy-structure
                  proxy. Values are converted from linear σ⁰ to decibels on
                  the server.
                </p>
                <div class="row">
                  <div class="col-md-6">
                    <div class="form-check">
                      <input v-model="selectedOutputs.vv_db" class="form-check-input" type="checkbox" id="vv_db" />
                      <label class="form-check-label" for="vv_db">
                        <strong>VV (dB)</strong><br>
                        <small class="text-muted">Co-polarized backscatter — roughness / vertical canopy structure</small>
                      </label>
                    </div>
                    <div class="form-check">
                      <input v-model="selectedOutputs.vh_db" class="form-check-input" type="checkbox" id="vh_db" />
                      <label class="form-check-label" for="vh_db">
                        <strong>VH (dB)</strong><br>
                        <small class="text-muted">Cross-polarized backscatter — volume scattering / biomass</small>
                      </label>
                    </div>
                  </div>
                  <div class="col-md-6">
                    <div class="form-check">
                      <input v-model="selectedOutputs.rvi" class="form-check-input" type="checkbox" id="rvi" />
                      <label class="form-check-label" for="rvi">
                        <strong>RVI (Radar Vegetation Index)</strong><br>
                        <small class="text-muted">4·VH / (VV + VH) — canopy density proxy</small>
                      </label>
                    </div>
                    <div class="form-check">
                      <input v-model="selectedOutputs.cr" class="form-check-input" type="checkbox" id="cr" />
                      <label class="form-check-label" for="cr">
                        <strong>Cross-pol ratio (VH/VV)</strong><br>
                        <small class="text-muted">Sensitive to crop type and growth stage</small>
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Native product (for landcover/terrain/soil/climate scenes only) -->
              <div v-if="isNativeOnly" class="output-group mb-3">
                <h6 class="text-primary">
                  <i class="fas fa-layer-group"></i> {{ nativeProductLabel }}
                </h6>
                <p class="small text-muted mb-2">
                  This scene is a specialty product — optical indices like NDVI
                  don't apply. We'll ingest the native product with its
                  appropriate colormap so it's ready to style and view.
                </p>
                <div class="form-check">
                  <input
                    v-model="selectedOutputs.native"
                    class="form-check-input"
                    type="checkbox"
                    id="nativeProduct"
                  />
                  <label class="form-check-label" for="nativeProduct">
                    <strong>{{ nativeProductLabel }}</strong>
                    <br>
                    <small class="text-muted">{{ nativeProductDescription }}</small>
                  </label>
                </div>
              </div>

              <!-- True Color Composite (optical only, not native-only, RGB available) -->
              <div v-if="!isSar && !isNativeOnly && canDoTrueColor" class="output-group mb-3">
                <div class="form-check">
                  <input
                    v-model="selectedOutputs.trueColor"
                    class="form-check-input"
                    type="checkbox"
                    id="trueColor"
                  />
                  <label class="form-check-label" for="trueColor">
                    <strong>True Color Composite (RGB)</strong>
                    <br>
                    <small class="text-muted">Natural color imagery for visual analysis and field inspection</small>
                  </label>
                </div>
              </div>

              <!-- Vegetation Indices (optical only, not native-only) -->
              <template v-if="!isSar && !isNativeOnly">
              <div class="output-group mb-3">
                <h6 class="text-success">🌱 Vegetation Indices</h6>
                
                <div class="row">
                  <div class="col-md-6">
                    <div v-if="indexAvailability.ndvi" class="form-check">
                      <input
                        v-model="selectedOutputs.ndvi"
                        class="form-check-input"
                        type="checkbox"
                        id="ndvi"
                      />
                      <label class="form-check-label" for="ndvi">
                        <strong>NDVI (Normalized Difference Vegetation Index)</strong>
                        <br>
                        <small class="text-muted">Plant health & biomass - most widely used vegetation index</small>
                      </label>
                    </div>

                    <div v-if="indexAvailability.evi" class="form-check">
                      <input
                        v-model="selectedOutputs.evi"
                        class="form-check-input"
                        type="checkbox"
                        id="evi"
                      />
                      <label class="form-check-label" for="evi">
                        <strong>EVI (Enhanced Vegetation Index)</strong>
                        <br>
                        <small class="text-muted">Better than NDVI in dense vegetation, less soil influence</small>
                      </label>
                    </div>

                    <div v-if="indexAvailability.ndre" class="form-check">
                      <input
                        v-model="selectedOutputs.ndre"
                        class="form-check-input"
                        type="checkbox"
                        id="ndre"
                      />
                      <label class="form-check-label" for="ndre">
                        <strong>NDRE (Normalized Difference Red Edge)</strong>
                        <br>
                        <small class="text-muted">Early stress detection, sensitive to chlorophyll content</small>
                      </label>
                    </div>
                  </div>

                  <div class="col-md-6">
                    <div v-if="indexAvailability.savi" class="form-check">
                      <input
                        v-model="selectedOutputs.savi"
                        class="form-check-input"
                        type="checkbox"
                        id="savi"
                      />
                      <label class="form-check-label" for="savi">
                        <strong>SAVI (Soil Adjusted Vegetation Index)</strong>
                        <br>
                        <small class="text-muted">Reduces soil background influence</small>
                      </label>
                    </div>

                    <div v-if="indexAvailability.gndvi" class="form-check">
                      <input
                        v-model="selectedOutputs.gndvi"
                        class="form-check-input"
                        type="checkbox"
                        id="gndvi"
                      />
                      <label class="form-check-label" for="gndvi">
                        <strong>GNDVI (Green NDVI)</strong>
                        <br>
                        <small class="text-muted">Chlorophyll content, less sensitive to atmospheric effects</small>
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Water & Moisture Indices -->
              <div
                v-if="indexAvailability.ndwi || indexAvailability.msi"
                class="output-group mb-3"
              >
                <h6 class="text-primary">💧 Water & Moisture Indices</h6>

                <div class="row">
                  <div class="col-md-6">
                    <div v-if="indexAvailability.ndwi" class="form-check">
                      <input
                        v-model="selectedOutputs.ndwi"
                        class="form-check-input"
                        type="checkbox"
                        id="ndwi"
                      />
                      <label class="form-check-label" for="ndwi">
                        <strong>NDWI (Normalized Difference Water Index)</strong>
                        <br>
                        <small class="text-muted">Water content in vegetation and soil moisture</small>
                      </label>
                    </div>
                  </div>

                  <div class="col-md-6">
                    <div v-if="indexAvailability.msi" class="form-check">
                      <input
                        v-model="selectedOutputs.msi"
                        class="form-check-input"
                        type="checkbox"
                        id="msi"
                      />
                      <label class="form-check-label" for="msi">
                        <strong>MSI (Moisture Stress Index)</strong>
                        <br>
                        <small class="text-muted">Plant water stress detection</small>
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Soil & Crop Indices -->
              <div class="output-group mb-3">
                <h6 class="text-warning">🌾 Soil & Crop Indices</h6>

                <div class="row">
                  <div class="col-md-6">
                    <div v-if="indexAvailability.ndsi" class="form-check">
                      <input
                        v-model="selectedOutputs.ndsi"
                        class="form-check-input"
                        type="checkbox"
                        id="ndsi"
                      />
                      <label class="form-check-label" for="ndsi">
                        <strong>NDSI (Normalized Difference Soil Index)</strong>
                        <br>
                        <small class="text-muted">Bare soil detection and soil type identification</small>
                      </label>
                    </div>
                  </div>

                  <div class="col-md-6">
                    <div class="form-check">
                      <input
                        v-model="selectedOutputs.customIndex"
                        class="form-check-input"
                        type="checkbox"
                        id="customIndex"
                      />
                      <label class="form-check-label" for="customIndex">
                        <strong>Custom Index</strong>
                        <br>
                        <small class="text-muted">Create your own spectral index</small>
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              </template>

              <!-- Custom Index Configuration -->
              <div v-if="!isSar && selectedOutputs.customIndex" class="custom-index-config mb-3">
                <div class="alert alert-info">
                  <h6><i class="fas fa-magic"></i> Custom Index Formula</h6>
                  <div class="form-group">
                    <label for="customIndexFormula">Formula (use b1, b2, b3, etc. for bands)</label>
                    <input
                      v-model="customIndexFormula"
                      id="customIndexFormula"
                      class="form-control"
                      placeholder="e.g., (b8-b4)/(b8+b4) for NDVI"
                    />
                    <small class="form-text text-muted">
                      <strong>Available bands:</strong> B02(Blue), B03(Green), B04(Red), B05(Red Edge), B08(NIR), B11(SWIR1), B12(SWIR2)
                    </small>
                  </div>
                  <div class="form-group">
                    <label for="customIndexName">Index Name</label>
                    <input
                      v-model="customIndexName"
                      id="customIndexName"
                      class="form-control"
                      placeholder="e.g., My Custom Index"
                    />
                  </div>
                </div>
              </div>
            </div>

            <!-- Multi-block save mode. Visible only when more than one
                 block is selected in the satellite drawer. Defaults to
                 "combined" (one dataset spanning the union); "per block"
                 reverts to the legacy N × M fan-out so power users can
                 still compare blocks side-by-side. -->
            <div v-if="multiBlockCount > 1" class="output-format mb-3">
              <h6 class="format-title">
                Multi-block save mode
                <span class="format-subtitle">{{ multiBlockCount }} blocks selected</span>
              </h6>
              <div class="format-options">
                <label class="format-option" :class="{ 'is-selected': saveMode === 'combined' }">
                  <input type="radio" value="combined" v-model="saveMode" />
                  <div>
                    <strong>Combined</strong> <span class="badge badge-recommended">Recommended</span>
                    <small class="d-block text-muted">
                      One dataset per output type, covering every selected block
                      at once. Best when you want a single layer to color or query.
                    </small>
                  </div>
                </label>
                <label class="format-option" :class="{ 'is-selected': saveMode === 'perBlock' }">
                  <input type="radio" value="perBlock" v-model="saveMode" />
                  <div>
                    <strong>Per block</strong>
                    <small class="d-block text-muted">
                      One dataset per (block × output). You'll get
                      {{ multiBlockCount }} × N datasets. Use when comparing
                      blocks against each other or organizing by site.
                    </small>
                  </div>
                </label>
              </div>
            </div>

            <!-- Output format — vector (default) vs raster ingest.
                 Only relevant when at least one selected output is an
                 analytical index that can be vectorized. NAIP true-color,
                 native land-cover etc. are raster-only by definition, so
                 hide the toggle to avoid offering a meaningless choice. -->
            <div v-if="hasVectorEligibleOutput" class="output-format mb-3">
              <h6 class="format-title">Output format</h6>
              <div class="format-options">
                <label class="format-option" :class="{ 'is-selected': !saveAsRaster }">
                  <input type="radio" :value="false" v-model="saveAsRaster" />
                  <div>
                    <strong>Vector grid</strong> <span class="badge badge-recommended">Recommended</span>
                    <small class="d-block text-muted">
                      Each output index becomes a fishnet of cells aligned to the source's native resolution. Faster to render, joinable across dates, queryable by zone.
                    </small>
                  </div>
                </label>
                <label class="format-option" :class="{ 'is-selected': saveAsRaster }">
                  <input type="radio" :value="true" v-model="saveAsRaster" />
                  <div>
                    <strong>Raster (GeoTIFF)</strong>
                    <small class="d-block text-muted">
                      Keep the full-resolution image. Useful if you want to download the raw raster for use in QGIS, ArcGIS, or another tool.
                    </small>
                  </div>
                </label>
              </div>
            </div>

            <!-- Processing Information -->
            <div class="processing-info">
              <div class="alert alert-success">
                <h6><i class="fas fa-info-circle"></i> What will be created:</h6>
                <div v-if="totalSelectedOutputs === 0" class="text-warning">
                  <i class="fas fa-exclamation-triangle"></i> Please select at least one output type
                </div>
                <div v-else>
                  <p><strong>{{ totalSelectedOutputs }} dataset(s)</strong> will be created:</p>
                  <ul class="mb-0">
                    <li v-if="selectedOutputs.trueColor"><strong>True Color Composite</strong> - RGB imagery for visual analysis</li>
                    <li v-if="selectedOutputs.ndvi"><strong>NDVI Dataset</strong> - Vegetation health and biomass</li>
                    <li v-if="selectedOutputs.evi"><strong>EVI Dataset</strong> - Enhanced vegetation analysis</li>
                    <li v-if="selectedOutputs.ndre"><strong>NDRE Dataset</strong> - Red edge analysis for early stress</li>
                    <li v-if="selectedOutputs.savi"><strong>SAVI Dataset</strong> - Soil-adjusted vegetation index</li>
                    <li v-if="selectedOutputs.gndvi"><strong>GNDVI Dataset</strong> - Green-based chlorophyll content</li>
                    <li v-if="selectedOutputs.ndwi"><strong>NDWI Dataset</strong> - Water content analysis</li>
                    <li v-if="selectedOutputs.msi"><strong>MSI Dataset</strong> - Moisture stress detection</li>
                    <li v-if="selectedOutputs.ndsi"><strong>NDSI Dataset</strong> - Soil type identification</li>
                    <li v-if="selectedOutputs.customIndex"><strong>{{ customIndexName || 'Custom Index' }} Dataset</strong> - Your custom spectral analysis</li>
                    <li v-if="selectedOutputs.native"><strong>{{ nativeProductLabel }}</strong> — {{ nativeProductDescription }}</li>
                    <li v-if="selectedOutputs.vv_db"><strong>VV (dB)</strong> - Sentinel-1 co-polarized backscatter</li>
                    <li v-if="selectedOutputs.vh_db"><strong>VH (dB)</strong> - Sentinel-1 cross-polarized backscatter</li>
                    <li v-if="selectedOutputs.rvi"><strong>RVI Dataset</strong> - Radar Vegetation Index</li>
                    <li v-if="selectedOutputs.cr"><strong>VH/VV Ratio</strong> - Cross-polarization ratio</li>
                  </ul>
                  <div class="mt-2 p-2 bg-light rounded">
                    <small><strong>Each dataset includes:</strong> GeoTIFF data, PNG preview, metadata, and appropriate colormap for visualization</small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="closeModal">
            Cancel
          </button>
          <button 
            type="button" 
            class="btn btn-primary"
            @click="createDatasets"
            :disabled="!baseDatasetName || totalSelectedOutputs === 0 || processing"
          >
            <span v-if="processing">
              <i class="fas fa-spinner fa-spin"></i> Creating {{ totalSelectedOutputs }} Dataset(s)...
            </span>
            <span v-else>
              <i class="fas fa-satellite"></i> Create {{ totalSelectedOutputs }} Dataset(s)
            </span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable no-console, max-len */
const fb = require('@/firebaseConfig.js')

export default {
  name: 'DatasetPreviewModal',
  props: {
    show: {
      type: Boolean,
      default: false
    },
    scene: {
      type: Object,
      default: null
    },
    block: {
      type: Object,
      default: null
    },
    // Multi-block save mode plumbing. The drawer passes the count of
    // effective blocks; when > 1 the modal exposes a "Combined vs
    // Per-block" save-mode toggle. Defaults preserve single-block UX.
    multiBlockCount: {
      type: Number,
      default: 1
    },
    multiBlockNames: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      baseDatasetName: '',
      datasetDescription: '',
      selectedFolderId: null,
      // Current "browse location" in the folder picker. null = farm root.
      browseFolderId: null,
      // Full folder tree for this farm — fetched fresh on mount since Vuex
      // `state.folders` is scoped to whatever folder the user was browsing.
      allFolders: [],
      foldersLoading: false,
      selectedOutputs: {
        trueColor: true, // Default to true color
        ndvi: false,
        evi: false,
        vv_db: false,
        vh_db: false,
        rvi: false,
        cr: false,
        ndre: false,
        savi: false,
        gndvi: false,
        ndwi: false,
        msi: false,
        ndsi: false,
        customIndex: false,
        native: false
      },
      customIndexFormula: '(b8-b4)/(b8+b4)',
      customIndexName: 'Custom Index',
      // Save format: false = vector fishnet (default, recommended);
      // true = raw GeoTIFF (legacy raster path, useful for download).
      saveAsRaster: false,
      // Multi-block save mode. Default 'combined' — one dataset
      // spanning the union of every selected block. 'perBlock' fans
      // out one dataset per block × output. Only relevant when
      // multiBlockCount > 1; otherwise hidden.
      saveMode: 'combined',
      processing: false
    }
  },
  computed: {
    /**
     * Are any of the currently-checked outputs ones the vector recipe can
     * actually produce? Used to hide the vector/raster format selector
     * for sources where it's meaningless (NAIP true color, native land
     * cover, etc.) — those are inherently raster.
     *
     * Mirrors VECTOR_INDEX_TYPES in SatellitePanel.vue's createMultipleDatasets.
     */
    hasVectorEligibleOutput() {
      const vectorKeys = ['ndvi', 'ndre', 'savi', 'evi', 'gndvi', 'ndwi', 'msi', 'ndsi', 'vv_db', 'vh_db', 'rvi', 'cr']
      return vectorKeys.some(k => this.selectedOutputs[k])
    },
    totalSelectedOutputs() {
      if (this.isNativeOnly) {
        return this.selectedOutputs.native ? 1 : 0
      }
      if (this.isSar) {
        const sarKeys = ['vv_db', 'vh_db', 'rvi', 'cr']
        return sarKeys.filter(k => this.selectedOutputs[k]).length
      }
      const opticalKeys = ['trueColor', 'ndvi', 'evi', 'ndre', 'savi', 'gndvi', 'ndwi', 'msi', 'ndsi', 'customIndex']
      return opticalKeys.filter(k => this.selectedOutputs[k]).length
    },
    // What asset aliases the scene exposes (friendly names).
    // Mirrors backend OPTICAL_ALIASES so the modal's gating matches what the
    // processor can actually resolve.
    availableAliases() {
      if (!this.scene?.assets) return new Set()
      const aliases = {
        blue: ['blue', 'B02'],
        green: ['green', 'B03'],
        red: ['red', 'B04'],
        rededge1: ['rededge1', 'rededge', 'B05'],
        nir: ['nir', 'nir08', 'B08', 'B8A'],
        swir16: ['swir16', 'B11', 'B06'],
        swir22: ['swir22', 'B12', 'B07'],
        vv: ['vv', 'VV'],
        vh: ['vh', 'VH']
      }
      const keys = Object.keys(this.scene.assets)
      const out = new Set()
      Object.entries(aliases).forEach(([alias, candidates]) => {
        if (candidates.some(c => keys.includes(c))) out.add(alias)
      })
      return out
    },
    // Is a True Color preview possible? True when scene has a pre-baked `visual`
    // asset (Sentinel-2) OR a full RGB triple (Landsat / HLS via MPC Data API).
    canDoTrueColor() {
      if (!this.scene?.assets) return false
      if (this.scene.assets.visual) return true
      const a = this.availableAliases
      return a.has('red') && a.has('green') && a.has('blue')
    },
    indexAvailability() {
      const a = this.availableAliases
      return {
        ndvi:   a.has('red') && a.has('nir'),
        evi:    a.has('red') && a.has('nir') && a.has('blue'),
        ndre:   a.has('rededge1') && a.has('nir'),
        savi:   a.has('red') && a.has('nir'),
        gndvi:  a.has('green') && a.has('nir'),
        ndwi:   a.has('green') && a.has('nir'),
        msi:    a.has('nir') && a.has('swir16'),
        ndsi:   a.has('green') && a.has('swir16')
      }
    },
    sceneSourceLabel() {
      if (!this.scene) return 'Satellite'
      return this.scene.source || this.scene.collectionMeta?.label || 'Satellite'
    },
    sceneResolution() {
      return this.scene?.collectionMeta?.resolution || '—'
    },
    hasCloudCover() {
      return typeof this.scene?.properties?.['eo:cloud_cover'] === 'number'
    },
    sceneDateLabel() {
      const p = this.scene?.properties || {}
      if (!p.datetime && (p.start_datetime || p.end_datetime)) return 'Period'
      return 'Date'
    },
    sceneDateDisplay() {
      const p = this.scene?.properties || {}
      const fmt = (d) => d
        ? new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
        : ''
      if (p.datetime) return this.formatDate(p.datetime)
      if (p.start_datetime && p.end_datetime) return `${fmt(p.start_datetime)} → ${fmt(p.end_datetime)}`
      if (p.start_datetime) return fmt(p.start_datetime)
      if (p.end_datetime) return fmt(p.end_datetime)
      return 'Unknown'
    },
    availableFolders() {
      // Prefer the locally-fetched full tree so nested navigation always works,
      // even if Vuex state.folders is scoped to a subfolder.
      const folders = this.allFolders.length
        ? this.allFolders
        : (this.$store?.state?.folders || [])
      return [...folders].sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')))
    },
    // All folders that have the current browse location as their parent.
    // Folders store their parent in `folderId`; 'root' or null/undefined = top-level.
    childFolders() {
      const parent = this.browseFolderId
      return this.availableFolders.filter(f => {
        const fp = f.folderId === 'root' || !f.folderId ? null : f.folderId
        return fp === parent
      })
    },
    // Build breadcrumb chain walking up from the browse location.
    folderBreadcrumbs() {
      const out = []
      let cur = this.browseFolderId
      const byId = Object.fromEntries(this.availableFolders.map(f => [f.id, f]))
      // Guard against cycles
      const seen = new Set()
      while (cur && !seen.has(cur)) {
        seen.add(cur)
        const folder = byId[cur]
        if (!folder) break
        out.unshift({ id: folder.id, name: folder.name || '(unnamed)' })
        const parent = folder.folderId === 'root' || !folder.folderId ? null : folder.folderId
        cur = parent
      }
      return out
    },
    currentFolderLabel() {
      if (this.browseFolderId === null) return 'Farm root'
      const last = this.folderBreadcrumbs[this.folderBreadcrumbs.length - 1]
      return last ? last.name : 'Farm root'
    },
    isSar() {
      if (!this.scene) return false
      if (this.scene.kind === 'sar') return true
      if (this.scene.collectionMeta?.kind === 'sar') return true
      return String(this.scene.collection || '').startsWith('sentinel-1')
    },
    sceneKind() {
      if (!this.scene) return 'optical'
      const k = this.scene.kind || this.scene.collectionMeta?.kind
      if (k) return k
      const coll = String(this.scene.collection || '').toLowerCase()
      if (coll.startsWith('sentinel-1')) return 'sar'
      if (/io-lulc|worldcover/.test(coll)) return 'landcover'
      if (/gnatsgo|ssurgo/.test(coll)) return 'soil'
      if (/3dep/.test(coll)) return 'terrain'
      if (/daymet/.test(coll)) return 'climate'
      return 'optical'
    },
    // True when this scene doesn't fit optical/SAR indices at all — we show
    // only the "native product" output for these (categorical land cover,
    // elevation, soil properties, climate grids, MODIS pre-computed VIs).
    isNativeOnly() {
      if (['landcover', 'soil', 'terrain', 'climate'].includes(this.sceneKind)) return true
      const coll = String(this.scene?.collection || '').toLowerCase()
      // MODIS MOD13Q1 ships pre-composited NDVI/EVI as assets, not raw bands.
      // Treat as native so we ingest those directly rather than trying to
      // compute indices from non-existent red/nir reflectance.
      if (/^modis-/.test(coll)) return true
      // Any optical scene where we can't assemble True Color AND can't
      // compute even NDVI (no red+nir) is a specialty product → native.
      if (this.sceneKind === 'optical' && !this.canDoTrueColor) {
        const a = this.availableAliases
        if (!a.has('red') || !a.has('nir')) return true
      }
      return false
    },
    nativeProductLabel() {
      const coll = String(this.scene?.collection || '').toLowerCase()
      if (/^modis-/.test(coll)) return 'MODIS Vegetation Index'
      switch (this.sceneKind) {
        case 'landcover': return 'Land Cover Classification'
        case 'soil': return 'Soil Property Raster'
        case 'terrain': return 'Elevation Model (DEM)'
        case 'climate': return 'Climate Grid'
        default: return 'Native Product'
      }
    },
    nativeProductDescription() {
      const coll = String(this.scene?.collection || '').toLowerCase()
      if (/^modis-/.test(coll)) {
        return 'Pre-composited NDVI (250 m, 16-day). Cloud-filtered, quality-masked — ready for phenology analysis.'
      }
      switch (this.sceneKind) {
        case 'landcover': return 'Categorical land-cover classes rendered with the product\'s official colormap.'
        case 'soil': return 'Gridded soil property (plant-available water by default) with a tuned colormap for field analysis.'
        case 'terrain': return 'Bare-earth elevation in meters with a terrain colormap.'
        case 'climate': return 'Daily climate variable (max temperature by default) with a diverging colormap.'
        default: return 'Product rendered with its native defaults.'
      }
    }
  },
  mounted() {
    // The parent uses v-if to mount this modal, so the `show` prop is already
    // true when we mount. The watcher below won't fire on initial mount because
    // there's no change. Call initializeForm here so per-kind defaults apply.
    if (this.show && this.scene && this.block) {
      this.initializeForm()
    }
    // Always fetch the full folder tree fresh — Vuex state.folders only holds
    // children of whichever folder the user was browsing.
    this.fetchAllFolders()
  },
  watch: {
    show(newVal) {
      if (newVal && this.scene && this.block) {
        this.initializeForm()
      }
    },
    scene(newVal) {
      // If parent swaps the scene while the modal is open, reinitialize.
      if (this.show && newVal && this.block) {
        this.initializeForm()
      }
    }
  },
  methods: {
    initializeForm() {
      const p = this.scene.properties || {}
      const dateSrc = p.datetime || p.end_datetime || p.start_datetime
      const acquisitionLabel = dateSrc
        ? new Date(dateSrc).toLocaleDateString()
        : (this.sceneKind === 'landcover' || this.sceneKind === 'terrain' || this.sceneKind === 'soil'
            ? 'static product'
            : 'unknown date')
      const source = this.scene.source || this.scene.collectionMeta?.label || 'Satellite'
      this.baseDatasetName = `${source} - ${this.block.name} - ${acquisitionLabel}`
      const cloud = p['eo:cloud_cover']
      const suffix = this.isSar
        ? ` Polarizations: ${(p['sar:polarizations'] || []).join('/')}.`
        : (typeof cloud === 'number' ? ` Cloud cover: ${cloud}%.` : '')
      this.datasetDescription = `${source} for ${this.block.name} (${acquisitionLabel}).${suffix}`

      // Always start the folder picker at the farm root so the user has a
      // predictable, full view of the tree — no surprises from stale Vuex
      // state that happens to be a deep subfolder.
      this.browseFolderId = null

      // Per-kind defaults
      if (this.isNativeOnly) {
        // Reset optical/SAR toggles; default the one sensible option
        Object.keys(this.selectedOutputs).forEach(k => { this.selectedOutputs[k] = false })
        this.selectedOutputs.native = true
      } else if (this.isSar) {
        this.selectedOutputs.trueColor = false
        if (!this.selectedOutputs.vv_db && !this.selectedOutputs.vh_db && !this.selectedOutputs.rvi && !this.selectedOutputs.cr) {
          this.selectedOutputs.vh_db = true
          this.selectedOutputs.rvi = true
        }
      } else {
        // Optical — turn off any indices whose bands this scene lacks, so a
        // stale default (like NDRE for Landsat) doesn't submit and 500.
        const avail = this.indexAvailability
        Object.keys(avail).forEach(key => {
          if (!avail[key]) this.selectedOutputs[key] = false
        })
        if (!this.canDoTrueColor) this.selectedOutputs.trueColor = false
      }
    },

    closeModal() {
      this.$emit('close')
    },

    /**
     * Load the full folder tree for this farm (all folders, no folderId filter).
     * Runs on mount and whenever the user clicks the home crumb so the picker
     * is always current — avoids stale state when the main app's folder scope
     * has drifted.
     */
    async fetchAllFolders() {
      const farmId = this.$store?.state?.farm?.id
      if (!farmId) return
      this.foldersLoading = true
      try {
        const snap = await fb.foldersCollection
          .where('farmId', '==', farmId)
          .orderBy('name', 'asc')
          .get()
        const folders = []
        snap.forEach(doc => {
          const f = doc.data()
          f.id = doc.id
          folders.push(f)
        })
        this.allFolders = folders
      } catch (err) {
        console.warn('Failed to fetch folder tree:', err)
      } finally {
        this.foldersLoading = false
      }
    },

    goToRootFolder() {
      this.browseFolderId = null
      // Refresh the tree in case anything changed (new folder created elsewhere, etc.)
      this.fetchAllFolders()
    },

    async createDatasets() {
      if (!this.baseDatasetName || this.totalSelectedOutputs === 0) {
        return
      }

      this.processing = true

      try {
        // Prepare the dataset configurations
        const datasetConfigs = []
        
        // True Color Composite
        if (this.selectedOutputs.trueColor) {
          datasetConfigs.push({
            name: `${this.baseDatasetName} - True Color`,
            description: `${this.datasetDescription} True color composite for visual analysis.`,
            type: 'trueColor',
            expression: null, // No band math needed for RGB
            bands: [2, 3, 4], // B02, B03, B04 (Blue, Green, Red)
            colormap: null // Natural color
          })
        }

        // True NDVI
        if (this.selectedOutputs.ndvi) {
          datasetConfigs.push({
            name: `${this.baseDatasetName} - NDVI`,
            description: `${this.datasetDescription} True Normalized Difference Vegetation Index (NDVI) calculated as (NIR - Red) / (NIR + Red) for precise plant health analysis.`,
            type: 'ndvi',
            expression: '(nir-red)/(nir+red)', // True NDVI formula
            expressionFallback: '(nir-red)/(nir+red+0.000001)', // Fallback with small constant
            expressionAlternative: '(nir-red)/(nir+red)', // Consistent NDVI approach
            bands: [4, 8], // B04 (Red), B08 (NIR) - true NDVI bands
            colormap: 'viridis' // Green to red colormap
          })
        }

        // EVI
        if (this.selectedOutputs.evi) {
          datasetConfigs.push({
            name: `${this.baseDatasetName} - EVI`,
            description: `${this.datasetDescription} Enhanced Vegetation Index for dense vegetation analysis.`,
            type: 'evi',
            expression: '2.5*(b8-b4)/(b8+6*b4-7.5*b2+1)', // Enhanced formula using numeric band indices
            bands: [2, 4, 8], // B02 (Blue), B04 (Red), B08 (NIR) - numeric band indices
            colormap: 'plasma' // Blue to yellow colormap
          })
        }

        // NDRE
        if (this.selectedOutputs.ndre) {
          datasetConfigs.push({
            name: `${this.baseDatasetName} - NDRE`,
            description: `${this.datasetDescription} Normalized Difference Red Edge for early stress detection.`,
            type: 'ndre',
            expression: '(b8-b5)/(b8+b5)', // (NIR-RedEdge)/(NIR+RedEdge) using numeric band indices
            bands: [5, 8], // B05 (Red Edge), B08 (NIR) - numeric band indices
            colormap: 'RdYlGn' // Red to green colormap
          })
        }

        // SAVI
        if (this.selectedOutputs.savi) {
          datasetConfigs.push({
            name: `${this.baseDatasetName} - SAVI`,
            description: `${this.datasetDescription} Soil Adjusted Vegetation Index to reduce soil influence.`,
            type: 'savi',
            expression: '1.5*(b8-b4)/(b8+b4+0.5)', // Soil adjusted formula using numeric band indices
            bands: [4, 8], // B04 (Red), B08 (NIR) - numeric band indices
            colormap: 'Greens' // Green colormap
          })
        }

        // GNDVI
        if (this.selectedOutputs.gndvi) {
          datasetConfigs.push({
            name: `${this.baseDatasetName} - GNDVI`,
            description: `${this.datasetDescription} Green NDVI for chlorophyll content analysis.`,
            type: 'gndvi',
            expression: '(b8-b3)/(b8+b3)', // (NIR-Green)/(NIR+Green) using numeric band indices
            bands: [3, 8], // B03 (Green), B08 (NIR) - numeric band indices
            colormap: 'YlGn' // Yellow to green colormap
          })
        }

        // NDWI
        if (this.selectedOutputs.ndwi) {
          datasetConfigs.push({
            name: `${this.baseDatasetName} - NDWI`,
            description: `${this.datasetDescription} Normalized Difference Water Index for water content analysis.`,
            type: 'ndwi',
            expression: '(b3-b8)/(b3+b8)', // (Green-NIR)/(Green+NIR) using numeric band indices
            bands: [3, 8], // B03 (Green), B08 (NIR) - numeric band indices
            colormap: 'Blues' // Blue colormap
          })
        }

        // MSI
        if (this.selectedOutputs.msi) {
          datasetConfigs.push({
            name: `${this.baseDatasetName} - MSI`,
            description: `${this.datasetDescription} Moisture Stress Index for plant water stress detection.`,
            type: 'msi',
            expression: 'b8/b11', // NIR/SWIR1 using actual band names
            bands: [8, 11], // NIR and SWIR1 band names
            colormap: 'Reds' // Red colormap (higher = more stress)
          })
        }

        // NDSI
        if (this.selectedOutputs.ndsi) {
          datasetConfigs.push({
            name: `${this.baseDatasetName} - NDSI`,
            description: `${this.datasetDescription} Normalized Difference Soil Index for soil type identification.`,
            type: 'ndsi',
            expression: '(b3-b11)/(b3+b11)', // (Green-SWIR1)/(Green+SWIR1) using actual band names
            bands: [3, 11], // Green and SWIR1 band names
            colormap: 'YlOrBr' // Yellow to brown colormap
          })
        }

        // ---- SAR products (Sentinel-1) ----
        if (this.selectedOutputs.vv_db) {
          datasetConfigs.push({
            name: `${this.baseDatasetName} - VV dB`,
            description: `${this.datasetDescription} VV backscatter in decibels (10·log10 σ⁰).`,
            type: 'vv_db',
            expression: '10*log10(vv)',
            bands: ['vv'],
            colormap: 'greys'
          })
        }
        if (this.selectedOutputs.vh_db) {
          datasetConfigs.push({
            name: `${this.baseDatasetName} - VH dB`,
            description: `${this.datasetDescription} VH backscatter in decibels (10·log10 σ⁰).`,
            type: 'vh_db',
            expression: '10*log10(vh)',
            bands: ['vh'],
            colormap: 'greys'
          })
        }
        if (this.selectedOutputs.rvi) {
          datasetConfigs.push({
            name: `${this.baseDatasetName} - RVI`,
            description: `${this.datasetDescription} Radar Vegetation Index: 4·VH / (VV + VH).`,
            type: 'rvi',
            expression: '(4*vh)/(vv+vh)',
            bands: ['vv', 'vh'],
            colormap: 'viridis'
          })
        }
        if (this.selectedOutputs.cr) {
          datasetConfigs.push({
            name: `${this.baseDatasetName} - VH-VV Ratio`,
            description: `${this.datasetDescription} Cross-polarization ratio VH/VV.`,
            type: 'cr',
            expression: 'vh/vv',
            bands: ['vv', 'vh'],
            colormap: 'viridis'
          })
        }

        // Custom Index
        if (this.selectedOutputs.customIndex && this.customIndexFormula && this.customIndexName) {
          datasetConfigs.push({
            name: `${this.baseDatasetName} - ${this.customIndexName}`,
            description: `${this.datasetDescription} Custom spectral index: ${this.customIndexFormula}`,
            type: 'custom',
            expression: this.customIndexFormula,
            bands: this.extractBandsFromFormula(this.customIndexFormula),
            colormap: 'viridis' // Default colormap
          })
        }

        // Native product (land cover / terrain / soil / climate)
        // Routes server-side through MPC's Data API with collection-specific
        // render defaults so categorical/non-RGB assets display correctly.
        if (this.selectedOutputs.native) {
          datasetConfigs.push({
            name: `${this.baseDatasetName} - ${this.nativeProductLabel}`,
            description: `${this.datasetDescription} ${this.nativeProductDescription}`,
            type: 'native',
            nativeKind: this.sceneKind,
            expression: null,
            bands: null,
            colormap: null
          })
        }

        console.log('🚀 Creating datasets with configs:', datasetConfigs)

        // Emit the dataset configurations to parent
        this.$emit('create', {
          baseName: this.baseDatasetName,
          description: this.datasetDescription,
          folderId: this.browseFolderId,
          // Force raster route when nothing in the selection can be
          // vectorized — the toggle is hidden in that case, so reading
          // `this.saveAsRaster` (default false) would incorrectly send
          // raster-only outputs down the vector recipe path.
          saveAsRaster: this.hasVectorEligibleOutput ? this.saveAsRaster : true,
          // 'combined' (default) → one dataset per output spanning every
          // selected block, 'perBlock' → existing N × M fan-out. Only
          // meaningful when multiBlockCount > 1; the modal hides the
          // toggle otherwise so the default is harmless.
          saveMode: this.multiBlockCount > 1 ? this.saveMode : 'combined',
          datasets: datasetConfigs
        })

      } catch (error) {
        console.error('Error creating datasets:', error)
        this.processing = false
      }
    },

    extractBandsFromFormula(formula) {
      // Extract band numbers from formula (b1, b2, b3, etc.)
      const bandMatches = formula.match(/b(\d+)/g)
      if (bandMatches) {
        return [...new Set(bandMatches.map(match => parseInt(match.substring(1))))]
      }
      return []
    },

    formatDate(dateString) {
      if (!dateString) return 'Unknown'
      
      const date = new Date(dateString)
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    }
  }
}
</script>

<style scoped>
/* Folder picker — breadcrumb + nested browser */
.folder-picker {
  border: 1px solid #ced4da;
  border-radius: 6px;
  background: #f8f9fa;
  overflow: hidden;
}
.folder-breadcrumb {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 8px 10px;
  background: #fff;
  border-bottom: 1px solid #e5e9f0;
  font-size: 0.85rem;
  flex-wrap: wrap;
}
.folder-crumb {
  background: none;
  border: none;
  padding: 2px 6px;
  border-radius: 3px;
  color: #495057;
  cursor: pointer;
  font-weight: 500;
  font-size: 0.85rem;
}
.folder-crumb:hover { background: #e9f3fe; color: #0b5ed7; }
.folder-crumb.is-current { background: #e9f3fe; color: #0b5ed7; }
.folder-crumb-sep { color: #adb5bd; font-size: 0.8rem; user-select: none; }
.folder-children {
  max-height: 180px;
  overflow-y: auto;
  background: #fff;
}
.folder-child {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-bottom: 1px solid #f1f3f5;
  color: #212529;
  text-decoration: none;
  font-size: 0.88rem;
}
.folder-child:last-child { border-bottom: none; }
.folder-child:hover { background: #f1f8ff; color: #0b5ed7; text-decoration: none; }
.folder-child-name { flex: 1; }
.folder-child-chevron { color: #adb5bd; font-size: 0.75rem; }
.folder-empty {
  padding: 12px;
  color: #6c757d;
  text-align: center;
}
.folder-save-here {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 9px 12px;
  background: #e9f3fe;
  border-top: 1px solid #cfe2ff;
  font-size: 0.85rem;
  color: #0b5ed7;
}
.folder-save-here strong { color: #0a4da8; }

.dataset-preview-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1050;
}

.modal-overlay {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
}

.modal-content {
  background: white;
  border-radius: 8px;
  width: 95%;
  max-width: 900px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #dee2e6;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-radius: 8px 8px 0 0;
}

.modal-header h5 {
  margin: 0;
  color: white;
}

.close {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: white;
}

.close:hover {
  color: #f8f9fa;
}

.modal-body {
  padding: 1.5rem;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  padding: 1rem 1.5rem;
  border-top: 1px solid #dee2e6;
}

.scene-info {
  background-color: #f8f9fa;
  padding: 1rem;
  border-radius: 4px;
  border-left: 4px solid #007bff;
}

.scene-info h6 {
  margin-bottom: 1rem;
  color: #495057;
}

.dataset-config h6 {
  margin-bottom: 1rem;
  color: #495057;
}

.output-group {
  background-color: #f8f9fa;
  padding: 1rem;
  border-radius: 4px;
  border: 1px solid #dee2e6;
}

.output-group h6 {
  margin-bottom: 1rem;
  font-size: 0.9rem;
  font-weight: 600;
}

.form-group {
  margin-bottom: 1rem;
}

.form-check {
  margin-bottom: 0.75rem;
}

.form-check-label {
  cursor: pointer;
  line-height: 1.4;
}

.form-check-label strong {
  color: #495057;
}

.form-check-label small {
  color: #6c757d;
  font-style: italic;
}

.custom-index-config .alert {
  margin-bottom: 0;
}

.processing-info .alert {
  margin-bottom: 0;
}

.processing-info ul {
  padding-left: 1.2rem;
}

.processing-info li {
  margin-bottom: 0.25rem;
}

.btn-primary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
  padding: 0.5rem 1.5rem;
}

.btn-primary:hover {
  background: linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%);
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
}

.btn-primary:disabled {
  background: #6c757d;
  transform: none;
  box-shadow: none;
}

/* Output format selector (vector vs raster) */
.output-format .format-subtitle {
  font-size: 0.72rem;
  color: #6c757d;
  margin-left: 8px;
  font-weight: normal;
  text-transform: none;
  letter-spacing: 0;
}
.output-format .format-title {
  font-size: 0.85rem;
  font-weight: 600;
  color: #495057;
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.format-options {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}
.format-option {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 10px 12px;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  background: #fff;
  cursor: pointer;
  margin: 0;
  transition: all 0.12s;
}
.format-option:hover { border-color: #0b5ed7; }
.format-option.is-selected {
  border-color: #0b5ed7;
  background: #f0f6ff;
  box-shadow: inset 0 0 0 1px #0b5ed7;
}
.format-option input[type="radio"] { margin-top: 4px; }
.format-option strong { font-size: 0.92rem; }
.format-option small { font-size: 0.78rem; line-height: 1.35; margin-top: 3px; }
.badge-recommended {
  display: inline-block;
  background: #d1ecf1;
  color: #0c5460;
  padding: 1px 6px;
  border-radius: 10px;
  font-size: 0.65rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-left: 4px;
}
@media (max-width: 600px) {
  .format-options { grid-template-columns: 1fr; }
}
</style>
